import React from "react";
import { MapPin, Link2, Users, Trash2, Copy, Pencil, CheckSquare, Repeat, ExternalLink } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { fmtDayLong, fmtRange, fmtDateTime, fmtDur } from "@/lib/time";

const PROVIDER_LABEL = { google: "Google Calendar", airtime: "AIRTIME — Événement manuel", icloud: "iCloud", ics: "ICS" };
const CAT_LABEL = { PERSONAL: "Personnel", MEETING: "Réunion", WORK: "Travail", GOLF: "Golf", BUSY: "Occupé", FREE: "Libre", EVENT: "Événement" };

function Row({ label, children }) {
  return <div><div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div><div className="text-sm mt-0.5">{children}</div></div>;
}

export default function EventDetail({ item, onClose, onEdit, onDelete, onDuplicate, onConvert }) {
  const open = !!item;
  const ev = item?.raw;
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto airtime-scroll" data-testid="event-detail">
        {ev && (
          <div className="space-y-6">
            <SheetHeader>
              <div className="flex items-center gap-2">
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${ev.transparency === "free" ? "bg-emerald-400" : "bg-rose-400"}`} />
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {ev.transparency === "free" ? "Libre" : "Occupé"}{ev.status === "tentative" ? " · provisoire" : ""} · {CAT_LABEL[ev.category] || ev.category}
                </span>
              </div>
              <SheetTitle className="font-display text-2xl leading-tight" data-testid="event-detail-title">{ev.title}</SheetTitle>
              <div className="text-sm text-muted-foreground capitalize">
                {item.allDay ? `${fmtDayLong(item.startDate)} · journée entière` : `${fmtDayLong(item.start)} · ${fmtRange(item.start, item.end)} · ${fmtDur(Math.round((item.end - item.start) / 60000))}`}
                {ev.is_occurrence && <span className="inline-flex items-center gap-1 ml-2 text-xs"><Repeat className="w-3 h-3" /> récurrent</span>}
              </div>
            </SheetHeader>

            {ev.location && <Row label="Lieu"><span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-muted-foreground" /> {ev.location}</span></Row>}
            {ev.meeting_url && <Row label="Visio"><a href={ev.meeting_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-primary hover:underline"><Link2 className="w-3.5 h-3.5" /> Rejoindre</a></Row>}
            {ev.attendees?.length > 0 && (
              <Row label={`Participants (${ev.attendees.length})`}>
                <div className="flex flex-wrap gap-1.5 mt-1">{ev.attendees.slice(0, 12).map((a) => (
                  <span key={a.email} className={`rounded-full border px-2 py-0.5 text-xs ${a.response === "accepted" ? "border-emerald-500/40 text-emerald-300" : a.response === "declined" ? "border-rose-500/40 text-rose-300 line-through" : "border-border text-muted-foreground"}`}>{a.name || a.email}</span>
                ))}</div>
              </Row>
            )}
            {ev.description && <Row label="Notes"><div className="whitespace-pre-wrap text-muted-foreground">{ev.description.slice(0, 1500)}</div></Row>}

            <div className="rounded-xl border border-border bg-background/40 p-4 space-y-2" data-testid="event-source">
              <Row label="Source">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-sm" style={{ background: ev.color || "#10b981" }} />
                  {PROVIDER_LABEL[ev.provider] || ev.provider}{ev.calendar_name && ev.provider !== "airtime" ? ` · ${ev.calendar_name}` : ""}
                  {ev.account_email && ev.provider !== "airtime" ? <span className="text-muted-foreground text-xs">({ev.account_email})</span> : null}
                </div>
              </Row>
              <Row label="Créé">{fmtDateTime(ev.created_at || ev.created_at_provider)}</Row>
              {ev.updated_at_provider && <Row label="Dernière modification (fournisseur)">{fmtDateTime(ev.updated_at_provider)}</Row>}
              {ev.html_link && <a href={ev.html_link} target="_blank" rel="noreferrer" data-testid="event-open-google" className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"><ExternalLink className="w-3 h-3" /> Ouvrir dans Google Calendar</a>}
            </div>

            {ev.editable ? (
              <div className="grid grid-cols-2 gap-2">
                <Btn testid="event-edit" icon={Pencil} onClick={() => onEdit(item)}>Modifier</Btn>
                <Btn testid="event-duplicate" icon={Copy} onClick={() => onDuplicate(item)}>Dupliquer</Btn>
                {!item.allDay && <Btn testid="event-convert" icon={CheckSquare} onClick={() => onConvert(item)}>Convertir en tâche</Btn>}
                <Btn testid="event-delete" icon={Trash2} danger onClick={() => onDelete(item)}>{ev.is_occurrence ? "Supprimer cette occurrence" : "Supprimer"}</Btn>
              </div>
            ) : (
              <div className="rounded-xl border border-border p-3 text-xs text-muted-foreground" data-testid="event-readonly-note">
                Événement externe en lecture seule dans AIRTIME pour ce sprint — modifie-le dans {PROVIDER_LABEL[ev.provider] || "sa source"} ; la synchronisation reflétera le changement.
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Btn({ icon: Icon, children, onClick, danger, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm transition-colors ${danger ? "border-rose-500/30 text-rose-300 hover:bg-rose-500/10" : "border-border hover:bg-accent"}`}>
      <Icon className="w-4 h-4" /> {children}
    </button>
  );
}
